/**
 * Directorio de Clanes — API Cliente
 * Endpoints backend:
 *  - GET /api/v1/public/directory/clans
 *  - GET /api/v1/public/directory/clans/:idOrSlug
 */
 
import createHttpClient from './HttpClient.js';
 
// Base URL: usa REACT_APP_API_URL si está definida; fallback a window.location.origin
const BASE_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL
  : (typeof window !== 'undefined' ? window.location.origin : '');
 
// Instancia del HttpClient común del proyecto (axios configurado)
const http = createHttpClient();
 
/**
 * Construye query string omitiendo undefined/null y serializando arrays como CSV.
 * @param {Record<string, any>} params
 * @returns {string}
 */
function buildQueryString(params) {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) q.set(k, v.join(','));
      return;
    }
    q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}
 
/**
 * Valida y normaliza parámetros comunes de paginación y filtros
 * @param {{page?: number, limit?: number, affiliationId?: number, affiliation?: string, elements?: string[]|string, rarityId?: number, rarity?: string, sort?: string}} params
 */
function normalizeListParams(params = {}) {
  let { page = 1, limit = 20, elements, sort } = params;
  page = Number.isFinite(+page) && +page >= 1 ? +page : 1;
  limit = Number.isFinite(+limit) ? Math.max(1, Math.min(100, +limit)) : 20;
 
  // elements puede venir como string CSV o array
  if (typeof elements === 'string') {
    elements = elements.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  } else if (Array.isArray(elements)) {
    elements = elements.map(s => String(s).trim().toLowerCase()).filter(Boolean);
  }
 
  // sort whitelist
  const allowedSort = new Set([
    'name asc', 'name desc',
    'rarity asc', 'rarity desc',
    'createdAt asc', 'createdAt desc'
  ]);
  if (sort && !allowedSort.has(sort)) {
    sort = undefined; // dejar que el backend aplique el default
  }
 
  return { ...params, page, limit, ...(elements ? { elements } : {}), ...(sort ? { sort } : {}) };
}
 
/**
 * Convierte el error del HttpClient en un Error estándar con propiedades.
 * @param {any} err
 */
function toStandardError(err) {
  const message = err?.message || 'HTTP Error';
  const e = new Error(message);
  if (err && typeof err === 'object') {
    if ('status' in err) e.status = err.status;
    if ('code' in err) e.code = err.code;
    if ('details' in err) e.details = err.details;
  }
  return e;
}
 
/**
 * Obtiene el listado paginado del directorio de clanes.
 * @param {{page?: number, limit?: number, affiliationId?: number, affiliation?: string, elements?: string[]|string, rarityId?: number, rarity?: string, sort?: string}} params
 * @returns {Promise<{ total:number, page:number, pages:number, limit:number, items:Array<any> }>}
 * @example
 * // Ejemplo: listar con filtros y orden
 * // const res = await fetchClansDirectory({ page:1, limit:12, elements:['fire','wind'], sort:'rarity desc' });
 */
export async function fetchClansDirectory(params = {}) {
  const normalized = normalizeListParams(params);
  const qs = buildQueryString(normalized);
  const url = `${BASE_URL}/api/v1/public/directory/clans${qs}`;
  try {
    const res = await http.get(url, {
      headers: { 'Accept': 'application/json' },
    });
    return res?.data ?? res;
  } catch (error) {
    throw toStandardError(error);
  }
}
 
/**
 * Obtiene el detalle de un clan por ID o slug.
 * @param {string|number} idOrSlug
 * @returns {Promise<any>}
 * @example
 * // const detail = await fetchClanDirectoryDetail('kaguya');
 */
export async function fetchClanDirectoryDetail(idOrSlug) {
  if (idOrSlug === undefined || idOrSlug === null || idOrSlug === '') {
    throw new Error('idOrSlug es requerido');
  }
  const url = `${BASE_URL}/api/v1/public/directory/clans/${encodeURIComponent(String(idOrSlug))}`;
  try {
    const res = await http.get(url, {
      headers: { 'Accept': 'application/json' },
    });
    return res?.data ?? res;
  } catch (error) {
    throw toStandardError(error);
  }
}
 
/**
 * Crea una técnica y la asocia a un clan (dashboard, requiere auth)
 * POST /api/v1/dashboard/characters/create/technique
 * @param {object} payload
 *  Ejemplo mínimo: { name, clanId | clanSlug }
 */
export async function createClanTechnique(payload = {}) {
  const url = `${BASE_URL}/api/v1/dashboard/characters/create/technique`;
  try {
    const res = await http.post(url, payload, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    });
    return res?.data ?? res;
  } catch (error) {
    throw toStandardError(error);
  }
}

/**
 * Obtiene lista pública de clanes (id y nombre) para selects
 * GET /api/v1/public/clans
 */
export async function fetchPublicClans() {
  const url = `${BASE_URL}/api/v1/public/clans`;
  try {
    const res = await http.get(url, {
      headers: { 'Accept': 'application/json' },
    });
    return res?.data ?? res;
  } catch (error) {
    throw toStandardError(error);
  }
}
 
/**
 * Pruebas manuales rápidas:
 *
 * // Lista
 * // fetchClansDirectory({ page:1, limit:20, elements:'fire,water', sort:'name asc' })
 * //   .then(console.log).catch(console.error);
 *
 * // Detalle
 * // fetchClanDirectoryDetail(123).then(console.log).catch(console.error);
 */